import { boot, endsWith, eraroCodesMap } from '@dazn/bootstrap-lib';

import {
    ResponseError,
    createFetchChapterError,
    createResponseError,
    isResponseError,
} from './errors';

const HTML_FILE_EXT = '.html';
const LAST_4XX_CODE = 499;

const isRetriable = (
    error: ResponseError | Error,
    remainingAttempts: number,
) => {
    if (remainingAttempts === 0) {
        return false;
    }

    if (isResponseError(error)) {
        return Number(error.internal) > LAST_4XX_CODE;
    }

    return true;
};

const fetchHTML = (fetch: typeof window.fetch, url: string) =>
    fetch(url)
        .then(
            response =>
                response.ok
                    ? response
                    : Promise.reject(createResponseError(response, url)),
            e =>
                Promise.reject(
                    createFetchChapterError(
                        `Failed to fetch ${url}`,
                        eraroCodesMap.internal.NetworkError,
                        e,
                    ),
                ),
        )
        .then(response =>
            response.text().then(text => ({
                url: response.url || url,
                html: text,
                chapter: response.headers.get('x-amz-meta-dazn-chapter'),
                version: response.headers.get('x-amz-meta-dazn-version'),
            })),
        );

const sleep = (time: number) =>
    new Promise(resolve => {
        setTimeout(resolve, time);
    });

const attemptFetch = (p: {
    fetch: typeof window.fetch;
    url: string;
    remainingAttempts: number;
    wait: number;
    multiplier: number;
}): ReturnType<typeof fetchHTML> =>
    fetchHTML(fetch, p.url).catch(error =>
        isRetriable(error, p.remainingAttempts)
            ? sleep(p.wait).then(() =>
                  attemptFetch({
                      fetch,
                      url: p.url,
                      remainingAttempts: p.remainingAttempts - 1,
                      wait: p.wait * p.multiplier,
                      multiplier: p.multiplier,
                  }),
              )
            : Promise.reject(error),
    );

const fetchChapterIndex = (
    fetch: boot.util.Fetch,
    url: string,
    { retry = 0, wait = 500, multiplier = 2 } = {},
) =>
    endsWith(url, HTML_FILE_EXT)
        ? attemptFetch({
              fetch,
              url,
              remainingAttempts: retry,
              wait,
              multiplier,
          })
        : Promise.reject(
              createFetchChapterError(
                  `Invalid url ${url}. Can load html only`,
                  eraroCodesMap.internal.BadRequest,
              ),
          );

export { fetchChapterIndex };
